import { createSlice } from '@reduxjs/toolkit'
import { StoreStatus, type User, type UserGroup, Role } from 'common/utils/types'

import {
  addConsultantCertificate,
  addUser,
  getUserGroups,
  getUsers,
  changeUserStatus,
  changeUserGroupStatus,
  addUserGroup,
} from './users-page.service'

const USERS_PER_PAGE = 11

export interface UsersState {
  status: StoreStatus
  addUserStatus: StoreStatus
  changeUserStatusStatus: StoreStatus
  addUserGroupStatus: StoreStatus
  users: {
    [key in Role]: User[] | UserGroup[]
  }
  userRoleChangeStatus: StoreStatus
  userGroups: UserGroup[]
  count: number
  addConsultantCertificateStatus: StoreStatus
  selectedConsultant: User | undefined
  usersPage: number
  changeGroupUsersStatus: StoreStatus
}

const initialState: UsersState = {
  status: StoreStatus.Loading,
  addUserStatus: StoreStatus.Idle,
  changeUserStatusStatus: StoreStatus.Idle,
  addUserGroupStatus: StoreStatus.Idle,
  users: {
    [Role.admin]: [],
    [Role.consultant]: [],
    [Role.stakeholder]: [],
    [Role.company]: [],
    [Role.vendor]: [],
  },
  userGroups: [],
  userRoleChangeStatus: StoreStatus.Loading,
  count: 0,
  addConsultantCertificateStatus: StoreStatus.Idle,
  selectedConsultant: undefined,
  usersPage: 0,
  changeGroupUsersStatus: StoreStatus.Idle,
}

export const UsersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    resetState: (state) => ({
      ...initialState,
    }),
    setUserRoleChangeStatus: (state, action) => {
      state.userRoleChangeStatus = action.payload
    },
    setSelectedConsultant: (state, action) => {
      state.selectedConsultant = action.payload
    },
    setUsersPage: (state, action) => {
      state.usersPage = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getUsers.pending, (state, { payload }) => ({
      ...state,
      status: StoreStatus.Loading,
    }))
    builder.addCase(getUsers.fulfilled, (state, { payload }) => {
      const updatedUsers = { ...state.users }

      payload.roles.forEach((role) => {
        updatedUsers[role] = payload.users.filter((user) => {
          if (role === Role.vendor && user.role !== Role.vendor) {
            return user.isVendor
          }
          return user.role === role
        })
      })

      return {
        ...state,
        users: updatedUsers,
        count: payload.count,
        status: StoreStatus.Succeeded,
      }
    })

    builder.addCase(getUsers.rejected, (state, { payload }) => {
      return {
        ...state,
        users: {
          ...state.users,
          [Role.admin]: [],
          [Role.consultant]: [],
          [Role.vendor]: [],
          [Role.company]: [],
        },
        count: 0,
        status: StoreStatus.Failed,
      }
    })
    builder.addCase(getUserGroups.pending, (state, { payload }) => ({
      ...state,
      status: StoreStatus.Loading,
    }))
    builder.addCase(getUserGroups.fulfilled, (state, { payload }) => {
      state.userGroups = payload.userGroups
      state.users = {
        ...state.users,
        [Role.stakeholder]: payload.userGroups,
      }
      state.count = payload.count
      state.status = StoreStatus.Succeeded
    })
    builder.addCase(getUserGroups.rejected, (state, { payload }) => {
      return {
        ...state,
        users: {
          ...state.users,
          [Role.stakeholder]: [],
        },
        count: 0,
        status: StoreStatus.Failed,
      }
    })
    builder.addCase(addConsultantCertificate.pending, (state, { payload }) => ({
      ...state,
      addConsultantCertificateStatus: StoreStatus.Loading,
    }))
    builder.addCase(addConsultantCertificate.fulfilled, (state, { payload }) => ({
      ...state,
      addConsultantCertificateStatus: StoreStatus.Succeeded,
    }))
    builder.addCase(addConsultantCertificate.rejected, (state, { payload }) => ({
      ...state,
      addConsultantCertificateStatus: StoreStatus.Failed,
    }))
    builder.addCase(addUser.pending, (state, { payload }) => ({
      ...state,
      addUserStatus: StoreStatus.Loading,
    }))
    builder.addCase(addUser.fulfilled, (state, { payload }) => {
      if (payload.reminder ?? payload.role === Role.stakeholder) {
        return {
          ...state,
          addUserStatus: StoreStatus.Succeeded,
        }
      } else {
        const user = payload
        delete user.reminder
        return {
          ...state,
          users: {
            ...state.users,
            [payload.role]: [user, ...state.users[payload.role]].slice(0, USERS_PER_PAGE),
          },
          count: state.count + 1,
          addUserStatus: StoreStatus.Succeeded,
        }
      }
    })
    builder.addCase(addUser.rejected, (state, { payload }) => ({
      ...state,
      addUserStatus: StoreStatus.Failed,
    }))
    builder.addCase(changeUserStatus.pending, (state, { payload }) => ({
      ...state,
      changeUserStatusStatus: StoreStatus.Loading,
    }))
    builder.addCase(changeUserStatus.fulfilled, (state, { payload }) => {
      state.users[payload.role] =
        (state.users[payload.role] as User[])?.map((user: User) => {
          if (user.id === payload.id) {
            return {
              ...user,
              status: payload.status,
            }
          }
          return user
        }) ?? []
      state.changeUserStatusStatus = StoreStatus.Succeeded
    })
    builder.addCase(changeUserStatus.rejected, (state, { payload }) => ({
      ...state,
      changeUserStatusStatus: StoreStatus.Failed,
    }))
    builder.addCase(changeUserGroupStatus.pending, (state, { payload }) => ({
      ...state,
      changeGroupUsersStatus: StoreStatus.Loading,
    }))
    builder.addCase(changeUserGroupStatus.fulfilled, (state, { payload }) => {
      return {
        ...state,
        users: {
          ...state.users,
          [Role.stakeholder]:
            (state.users[Role.stakeholder] as UserGroup[])?.map((user: UserGroup) => {
              if (user.id === payload.id) {
                return {
                  ...user,
                  status: payload.status,
                }
              }
              return user
            }) ?? [],
        },
        count: state.count - 1,
        changeGroupUsersStatus: StoreStatus.Succeeded,
      }
    })
    builder.addCase(changeUserGroupStatus.rejected, (state, { payload }) => ({
      ...state,
      changeGroupUsersStatus: StoreStatus.Failed,
    }))
    builder.addCase(addUserGroup.pending, (state, { payload }) => ({
      ...state,
      addUserGroupStatus: StoreStatus.Loading,
    }))
    builder.addCase(addUserGroup.fulfilled, (state, { payload }) => {
      state.addUserGroupStatus = StoreStatus.Succeeded
      state.count = state.count + 1
      state.users[Role.stakeholder] = [payload, ...(state.users[Role.stakeholder] as UserGroup[])].slice(
        0,
        USERS_PER_PAGE,
      )
    })
    builder.addCase(addUserGroup.rejected, (state, { payload }) => ({
      ...state,
      addUserGroupStatus: StoreStatus.Failed,
    }))
  },
})

export const { resetState, setUserRoleChangeStatus, setSelectedConsultant, setUsersPage } = UsersSlice.actions

export default UsersSlice.reducer
